/**
 * SessionDetailScreen — Analytics breakdown for a single retreat run.
 * UI-INV-005: Leaf component, props only, no engine imports.
 *
 * The `reflection` text is read by the parent (renderScreen.tsx) and injected
 * as a prop so this component stays testable in isolation (SOLID-01).
 */
import type { RetreatSession, ContainerRecord } from "@retreat/core";
import { derivePrideStats } from "../utils/prideStats";
import { fmtDurationCompact } from "../utils/dateFormatting";
import { SESSION_STATUS } from "../constants";
import styles from "./SessionDetailScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { calculateCircumference } from "../utils/geometry";

const fmtDuration = fmtDurationCompact;

function fmtDate(session: RetreatSession): string {
  const start = new Date(session.startedAt);
  const end = session.endedAt ? new Date(session.endedAt) : null;
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (!end || start.toDateString() === end.toDateString()) {
    return start.toLocaleDateString(undefined, opts);
  }
  return `${start.toLocaleDateString(undefined, opts)}–${end.toLocaleDateString(undefined, opts)}`;
}

// ── Completion ring ─────────────────────────────────────────────────────────

const RING_R = 46;
const RING_STROKE_PADDING = 6;          // half stroke width — keeps the circle un-clipped
const RING_CENTER = RING_R + RING_STROKE_PADDING;  // 52
const RING_SIZE = RING_CENTER * 2;      // 104 — viewBox width/height
const RING_C = calculateCircumference(RING_R);

function CompletionRing({ pct }: { pct: number }) {
  const clamped = Math.min(100, Math.max(0, pct));
  const offset = RING_C * (1 - clamped / 100);
  return (
    <div className={styles.ringWrap}>
      <svg viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} className={styles.ringSvg} aria-hidden="true">
        <circle cx={RING_CENTER} cy={RING_CENTER} r={RING_R} className={styles.ringTrack} />
        <circle
          cx={RING_CENTER} cy={RING_CENTER} r={RING_R}
          className={styles.ringFill}
          strokeDasharray={RING_C.toFixed(1)}
          strokeDashoffset={offset.toFixed(1)}
          transform={`rotate(-90 ${RING_CENTER} ${RING_CENTER})`}
        />
      </svg>
      <div className={styles.ringInner}>
        <span className={styles.ringPct}>{Math.round(clamped)}%</span>
        <span className={styles.ringLabel}>of schedule</span>
      </div>
    </div>
  );
}

// ── Stat tile ───────────────────────────────────────────────────────────────

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.statTile}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue}>{value}</span>
    </div>
  );
}

// ── Activity composition bar ─────────────────────────────────────────────────

type ActivitySegment = { label: string; seconds: number; type: "meditation" | "walking" | "sleep" | "other" };

function ActivityBar({ segments, total }: { segments: ActivitySegment[]; total: number }) {
  if (total === 0 || segments.length === 0) return null;
  return (
    <div className={styles.activityBarWrap} aria-label="Activity composition">
      <div className={styles.activityBar}>
        {segments.map((seg) => (
          <div
            key={seg.type}
            className={styles.activityBarSegment}
            data-type={seg.type}
            style={{ width: `${(seg.seconds / total) * 100}%` }}
            aria-label={`${seg.label}: ${fmtDuration(seg.seconds)}`}
          />
        ))}
      </div>
      <div className={styles.activityBarLegend}>
        {segments.map((seg) => (
          <span key={seg.type} className={styles.activityLegendItem} data-type={seg.type}>
            {seg.label} · {fmtDuration(seg.seconds)}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Timeline row ─────────────────────────────────────────────────────────────

function TimelineRow({ record, isLast }: { record: ContainerRecord; isLast: boolean }) {
  return (
    <div className={styles.timelineRow}>
      <div className={styles.timelineSpine}>
        <div className={`${styles.dot} ${record.isComplete ? styles.dotDone : styles.dotStopped}`} />
        {!isLast && <div className={styles.spineLine} />}
      </div>
      <div className={styles.timelineBody}>
        <div className={styles.timelineTop}>
          <span className={styles.timelineName}>{record.containerName}</span>
          <span className={styles.timelineDuration}>{fmtDuration(record.actualDurationSeconds)}</span>
        </div>
        {!record.isComplete && (
          <span className={styles.timelineStopped}>
            stopped · {fmtDuration(record.scheduledDurationSeconds)} planned
          </span>
        )}
      </div>
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

type Props = {
  session: RetreatSession;
  reflection?: string | null;
  onBack: () => void;
};

// ── Main component ────────────────────────────────────────────────────────────

export function SessionDetailScreen({ session, reflection, onBack }: Props) {
  const stats = derivePrideStats(session.containers);
  const totalActual = session.containers.reduce((acc, c) => acc + c.actualDurationSeconds, 0);
  const totalPlanned = session.totalScheduledSeconds
    ?? session.containers.reduce((acc, c) => acc + c.scheduledDurationSeconds, 0);
  const completionPct = totalPlanned > 0 ? (totalActual / totalPlanned) * 100 : 0;
  const isComplete = session.status === SESSION_STATUS.COMPLETED;

  const otherSeconds = Math.max(
    0,
    totalActual - stats.meditationSeconds - stats.walkingSeconds - stats.sleepSeconds,
  );

  const statTiles: { label: string; value: string }[] = [];
  if (stats.meditationSeconds > 0) statTiles.push({ label: "Sitting", value: fmtDuration(stats.meditationSeconds) });
  if (stats.walkingSeconds > 0) statTiles.push({ label: "Walking", value: fmtDuration(stats.walkingSeconds) });
  if (stats.sleepSeconds > 0) statTiles.push({ label: "Sleep", value: fmtDuration(stats.sleepSeconds) });
  if (otherSeconds > 0) statTiles.push({ label: "Other", value: fmtDuration(otherSeconds) });

  const activitySegments: ActivitySegment[] = [];
  if (stats.meditationSeconds > 0) activitySegments.push({ label: "Sitting", seconds: stats.meditationSeconds, type: "meditation" });
  if (stats.walkingSeconds > 0) activitySegments.push({ label: "Walking", seconds: stats.walkingSeconds, type: "walking" });
  if (stats.sleepSeconds > 0) activitySegments.push({ label: "Sleep", seconds: stats.sleepSeconds, type: "sleep" });
  if (otherSeconds > 0) activitySegments.push({ label: "Other", seconds: otherSeconds, type: "other" });

  return (
    <div className={styles.screen} data-testid={TEST_IDS.SESSION_DETAIL_SCREEN}>
      <div className={styles.meta}>
        <button className={styles.backBtn} onClick={onBack} type="button" data-testid={TEST_IDS.BACK_BTN}>
          ← back
        </button>
      </div>

      <div className={styles.content}>
        <div className={styles.retreatName}>{session.retreatName}</div>

        {/* Hero: duration (left) + completion ring (right) */}
        <div className={styles.hero}>
          <div className={styles.heroLeft}>
            <span className={styles.heroNum}>{fmtDuration(totalActual)}</span>
            <span className={styles.heroSub}>{fmtDate(session)}</span>
            <span className={`${styles.heroBadge} ${isComplete ? styles.heroBadgeDone : styles.heroBadgePartial}`}>
              {isComplete ? "completed" : "ended early"}
            </span>
          </div>
          <CompletionRing pct={completionPct} />
        </div>

        {/* Activity composition bar */}
        <ActivityBar segments={activitySegments} total={totalActual} />

        {/* Stat tiles — 2-column grid */}
        {statTiles.length > 0 && (
          <div className={styles.statsGrid}>
            {statTiles.map((tile) => (
              <StatTile key={tile.label} label={tile.label} value={tile.value} />
            ))}
          </div>
        )}

        {/* Container timeline */}
        {session.containers.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Timeline</div>
            <div className={styles.timeline}>
              {session.containers.map((c, i) => (
                <TimelineRow
                  key={`${c.containerId}-${i}`}
                  record={c}
                  isLast={i === session.containers.length - 1}
                />
              ))}
            </div>
          </div>
        )}

        {/* Reflection notes */}
        {reflection && reflection.trim().length > 0 && (
          <div className={styles.section} data-testid={TEST_IDS.SESSION_DETAIL_REFLECTION}>
            <div className={styles.sectionLabel}>Your notes</div>
            <p className={styles.reflectionText}>{reflection.trim()}</p>
          </div>
        )}
      </div>
    </div>
  );
}
